import { MenuItemVariantDto } from '../../generated-api';
import { CartOrderLine } from '../../types/cart';

export interface BundleDefinition {
  variantId: string;
  name: string;
  price: number;
  takeawayPrice?: number;
  menuSectionId?: string;
  sectionProductId?: string;
  courseId?: string;
  components: {
    productSizeId: string;
    quantity: number;
    effectivePrice: number;
    effectiveTakeawayPrice: number;
  }[];
  savings: number;
  savingsTakeaway: number;
}

export interface BundleMatch {
  bundle: BundleDefinition;
  instances: number;
}

/**
 * Builds bundle definitions from menu products.
 * Filters for composite products with autoApplyBundlePrice and computes savings.
 */
export function buildBundleDefinitions(allProducts: MenuItemVariantDto[]): BundleDefinition[] {
  const definitions: BundleDefinition[] = [];

  for (const product of allProducts) {
    if (!product.isComposite || !product.autoApplyBundlePrice) continue;
    if (!product.components || product.components.length === 0) continue;

    const components = product.components.map((c) => ({
      productSizeId: c.productSizeId,
      quantity: c.quantity,
      effectivePrice: c.effectivePrice,
      effectiveTakeawayPrice: c.effectiveTakeawayPrice,
    }));

    const sumDefaultPrice = product.components.reduce((acc, c) => acc + c.defaultPrice * c.quantity, 0);
    const sumEffectivePrice = product.components.reduce((acc, c) => acc + c.effectivePrice * c.quantity, 0);
    const sumDefaultTakeawayPrice = product.components.reduce((acc, c) => acc + c.defaultTakeawayPrice * c.quantity, 0);
    const sumEffectiveTakeawayPrice = product.components.reduce(
      (acc, c) => acc + c.effectiveTakeawayPrice * c.quantity,
      0
    );

    const savings = sumDefaultPrice - sumEffectivePrice;
    const savingsTakeaway = sumDefaultTakeawayPrice - sumEffectiveTakeawayPrice;

    // Skip bundles with no savings
    if (savings <= 0 && savingsTakeaway <= 0) continue;

    definitions.push({
      variantId: product.variantLinkId,
      name: product.name,
      price: product.price,
      takeawayPrice: product.takeawayPrice,
      menuSectionId: product.menuSectionId,
      sectionProductId: product.sectionProductId,
      courseId: product.courseId,
      components,
      savings,
      savingsTakeaway,
    });
  }

  // Sort by savings descending for greedy assignment priority
  definitions.sort((a, b) => b.savings - a.savings);

  return definitions;
}

/**
 * Detects which bundles can be formed from current cart order lines.
 * Among matchable bundles, prefers the one covering the most components
 * (most specific match). Falls back to savings descending as tiebreaker.
 * Returns matched bundles with instance counts.
 */
export function detectBundles(orderLines: CartOrderLine[], definitions: BundleDefinition[]): BundleMatch[] {
  // Build availability map: variantId → total quantity
  const available = new Map<string, number>();
  for (const line of orderLines) {
    const current = available.get(line.itemVariantId) ?? 0;
    available.set(line.itemVariantId, current + line.quantity);
  }

  // Sort by most components first, then savings as tiebreaker
  const sorted = [...definitions].sort((a, b) => {
    const compDiff = b.components.length - a.components.length;
    if (compDiff !== 0) return compDiff;
    return b.savings - a.savings;
  });

  const matches: BundleMatch[] = [];

  for (const bundle of sorted) {
    // Determine how many instances of this bundle can be formed
    let maxInstances = Infinity;

    for (const component of bundle.components) {
      const availableQty = available.get(component.productSizeId) ?? 0;
      const possibleInstances = Math.floor(availableQty / component.quantity);
      maxInstances = Math.min(maxInstances, possibleInstances);
    }

    if (maxInstances <= 0 || maxInstances === Infinity) continue;

    // Greedily claim the items
    for (const component of bundle.components) {
      const current = available.get(component.productSizeId)!;
      available.set(component.productSizeId, current - component.quantity * maxInstances);
    }

    matches.push({ bundle, instances: maxInstances });
  }

  return matches;
}
